import { createFileRoute, Outlet, useMatches, useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CreditCard, FileMinus2, FileText, Pencil, Receipt, RefreshCw, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { BillingEditSheet } from '@/components/settings/billing/BillingEditSheet';
import { useHasPermission } from '@/hooks/usePermissions';
import type { BillingRouteTab } from '@/components/settings/billing/BillingReadOnlyTab';

/**
 * Parent route for the unified Invoice settings panel (PR 9 / spec §10).
 *
 * Acts as both:
 *   1. A fallback that renders an in-page tab strip and lets each child route
 *      mount its own `<Tab />` content via `<Outlet />`. The tab strip is
 *      always visible — child routes only render the inner panel.
 *   2. A redirect from the bare `/settings/billing` path to the first tab,
 *      `/settings/billing/invoices`, via the index child route.
 *
 * Subscription / plan management has moved to `/settings/subscription`.
 */
export const Route = createFileRoute('/_dashboard/dashboard/settings/billing')({
  component: BillingPanelLayout,
});

const TABS: ReadonlyArray<{
  value: BillingRouteTab;
  to:
    | '/dashboard/settings/billing/invoices'
    | '/dashboard/settings/billing/receipts'
    | '/dashboard/settings/billing/deposits'
    | '/dashboard/settings/billing/payments'
    | '/dashboard/settings/billing/credit-memos'
    | '/dashboard/settings/billing/quickbooks';
  label: string;
  icon: LucideIcon;
}> = [
  {
    value: 'invoices',
    to: '/dashboard/settings/billing/invoices',
    label: 'Invoices',
    icon: FileText,
  },
  {
    value: 'receipts',
    to: '/dashboard/settings/billing/receipts',
    label: 'Receipts',
    icon: Receipt,
  },
  {
    value: 'deposits',
    to: '/dashboard/settings/billing/deposits',
    label: 'Deposits',
    icon: Wallet,
  },
  {
    value: 'payments',
    to: '/dashboard/settings/billing/payments',
    label: 'Payment Methods',
    icon: CreditCard,
  },
  {
    value: 'credit-memos',
    to: '/dashboard/settings/billing/credit-memos',
    label: 'Credit Memos',
    icon: FileMinus2,
  },
  {
    value: 'quickbooks',
    to: '/dashboard/settings/billing/quickbooks',
    label: 'Accounting Sync',
    icon: RefreshCw,
  },
];

function BillingPanelLayout() {
  const router = useRouter();
  const matches = useMatches();
  const [editOpen, setEditOpen] = useState(false);
  const canViewBilling = useHasPermission('billing:view');
  const canEditBilling = useHasPermission('billing:edit');
  const activePath = useMemo(() => {
    const child = matches.find((m) =>
      m.routeId.startsWith('/_dashboard/dashboard/settings/billing/'),
    );
    return child?.pathname ?? '';
  }, [matches]);
  const activeTab = TABS.find((tab) => activePath === tab.to || activePath.startsWith(tab.to));
  const currentTab = activeTab ?? TABS[0];

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Invoice settings</h1>
              <p className="text-xs text-muted-foreground">
                Configure customer invoices, payments, deposits, credits, and accounting sync.
              </p>
            </div>
            {canEditBilling ? (
              <Button
                variant="outline"
                size="icon-sm"
                className="shrink-0"
                title="Edit"
                // Names what the sheet edits — it opens on the current tab —
                // and keeps the page-level name the permission e2e looks for.
                aria-label={`Edit invoice settings: ${currentTab.label}`}
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="size-3.5" />
              </Button>
            ) : null}
          </>
        }
      >
        {canViewBilling ? (
          <Tabs
            value={currentTab.value}
            onValueChange={(value) => {
              const next = TABS.find((tab) => tab.value === value);
              if (next) void router.navigate({ to: next.to });
            }}
          >
            <TabsList
              variant="line"
              className="h-auto w-full justify-start rounded-none border-b px-0 pb-0"
            >
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    <Icon className="size-3.5" />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <Outlet />
          </Tabs>
        ) : (
          <div className="flex min-h-64 items-center justify-center p-6 text-center">
            <p className="text-sm text-muted-foreground">
              You do not have permission to view billing settings.
            </p>
          </div>
        )}
      </DetailPageLayout>
      <BillingEditSheet
        open={canEditBilling && editOpen}
        tab={currentTab.value}
        onOpenChange={setEditOpen}
      />
    </>
  );
}
